import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PhoneOff, Loader2, Trash2, Upload, Download, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { DoNotCallEntry } from '@/types/doNotCall';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';

export default function DoNotCallSettings() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [reason, setReason] = useState('customer_requested');
  const [notes, setNotes] = useState('');
  const [page, setPage] = useState(0);
  const [uploadMode, setUploadMode] = useState<'single' | 'bulk'>('single');
  const [csvData, setCsvData] = useState<string>('');
  const [bulkReason, setBulkReason] = useState('customer_requested');
  const [uploadProgress, setUploadProgress] = useState<{
    total: number;
    processed: number;
    success: number;
    errors: number;
  } | null>(null);
  const ITEMS_PER_PAGE = 15;

  // Fetch DNC list
  const { data: dncData, isLoading, error } = useQuery({
    queryKey: ['do-not-call-list', page],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/do-not-call-list?limit=${ITEMS_PER_PAGE}&offset=${page * ITEMS_PER_PAGE}`,
        {
          headers: { Authorization: `Bearer ${session.access_token}` }
        }
      );
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        if (res.status === 500 && errorData.error?.includes('relation "do_not_call_list" does not exist')) {
          throw new Error('Do Not Call feature is being set up. Please try again in a moment.');
        }
        throw new Error(errorData.error || 'Failed to fetch DNC list');
      }
      return await res.json();
    },
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Don't retry if it's a database setup issue
      if (error.message.includes('being set up')) {
        return false;
      }
      return failureCount < 3;
    }
  });

  // Add number mutation
  const addNumberMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      // Format phone number to E.164
      let formattedPhone = phoneNumber.replace(/\D/g, '');
      if (!formattedPhone.startsWith('+')) {
        if (formattedPhone.length === 10) {
          formattedPhone = `+1${formattedPhone}`;
        } else if (formattedPhone.length === 11 && formattedPhone.startsWith('1')) {
          formattedPhone = `+${formattedPhone}`;
        } else {
          throw new Error('Invalid phone number format');
        }
      }
      
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/do-not-call-list`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}` 
        },
        body: JSON.stringify({
          phone_number: formattedPhone,
          reason,
          notes: notes.trim() || undefined
        })
      });
      
      if (res.status === 409) {
        throw new Error('This number is already on your Do Not Call list');
      }
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to add number');
      }
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['do-not-call-list'] });
      toast.success('Number added to Do Not Call list');
      setPhoneNumber('');
      setNotes('');
      setReason('customer_requested');
    },
    onError: (err: Error) => {
      toast.error(err.message);
    }
  });

  // Bulk upload mutation
  const bulkUploadMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      // Parse CSV data
      const lines = csvData.trim().split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        throw new Error('CSV must have at least a header row and one data row');
      }
      
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const phoneNumberIndex = headers.findIndex(h => h.includes('phone') || h.includes('number'));
      
      if (phoneNumberIndex === -1) {
        throw new Error('CSV must contain a "phone" or "number" column');
      }
      
      const phoneNumbers = lines.slice(1).map((line, index) => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const phone = values[phoneNumberIndex];
        if (!phone) {
          console.warn(`Row ${index + 2}: Empty phone number`);
          return null;
        }
        return phone;
      }).filter((phone): phone is string => phone !== null && phone.length > 0);
      
      if (phoneNumbers.length === 0) {
        throw new Error('No valid phone numbers found in CSV');
      }
      
      console.log('Processing bulk upload:', {
        totalNumbers: phoneNumbers.length,
        phoneNumbers: phoneNumbers.slice(0, 5), // Log first 5 for debugging
        reason: bulkReason
      });
      
      setUploadProgress({
        total: phoneNumbers.length,
        processed: 0,
        success: 0,
        errors: 0
      });
      
      let successCount = 0;
      let errorCount = 0;
      
      // Process each phone number
      for (let i = 0; i < phoneNumbers.length; i++) {
        const phone = phoneNumbers[i];
        
        try {
          // Format phone number to E.164
          let formattedPhone = phone.replace(/\D/g, '');
          if (!formattedPhone.startsWith('+')) {
            if (formattedPhone.length === 10) {
              formattedPhone = `+1${formattedPhone}`;
            } else if (formattedPhone.length === 11 && formattedPhone.startsWith('1')) {
              formattedPhone = `+${formattedPhone}`;
            } else {
              throw new Error(`Invalid phone number format: ${phone}`);
            }
          }
          
          console.log(`Processing phone ${i + 1}/${phoneNumbers.length}: ${phone} -> ${formattedPhone}`);
          
          const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/do-not-call-list`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}` 
            },
            body: JSON.stringify({
              phone_number: formattedPhone,
              reason: bulkReason,
              notes: `Bulk uploaded - ${phone}`
            })
          });
          
          if (res.status === 409) {
            // Number already exists, count as success
            console.log(`Phone ${phone} already exists in DNC list`);
            successCount++;
          } else if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(`Failed to add ${phone}: ${errorData.error || res.statusText}`);
          } else {
            console.log(`Successfully added ${phone} to DNC list`);
            successCount++;
          }
        } catch (error) {
          console.error(`Error processing phone number ${phone}:`, error);
          errorCount++;
        }
        
        setUploadProgress(prev => prev ? {
          ...prev,
          processed: i + 1,
          success: successCount,
          errors: errorCount
        } : null);
      }
      
      return { successCount, errorCount, total: phoneNumbers.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['do-not-call-list'] });
      setCsvData('');
      setUploadProgress(null);
      
      if (data.errorCount === 0) {
        toast.success(`✅ Bulk upload complete: ${data.successCount} numbers added to Do Not Call list`);
      } else {
        toast.success(`⚠️ Bulk upload complete: ${data.successCount} numbers added, ${data.errorCount} failed`);
      }
    },
    onError: (err: Error) => {
      setUploadProgress(null);
      toast.error(err.message);
    }
  });

  // Delete number mutation
  const deleteNumberMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/do-not-call-list`, {
        method: 'DELETE',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}` 
        },
        body: JSON.stringify({ id })
      });
      
      if (!res.ok) throw new Error('Failed to remove number');
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['do-not-call-list'] });
      toast.success('Number removed from Do Not Call list');
    },
    onError: (err: Error) => {
      toast.error(err.message);
    }
  });

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCsvData(text);
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const template = 'phone_number,notes\n+15551234567,Customer requested\n+15559876543,Wrong number';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'do-not-call-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const entries = dncData?.data || [];
  const totalCount = dncData?.count || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const formatReasonDisplay = (reason?: string) => {
    if (!reason) return 'Unknown';
    return reason.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center text-2xl">
          <PhoneOff className="mr-3 h-7 w-7 text-red-600" />
          Do Not Call List
        </CardTitle>
        <CardDescription>
          Numbers on this list will never be contacted by your AI Employee
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Stats */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-lg font-medium text-red-800">
            {totalCount} numbers currently blocked
          </p>
          <p className="text-sm text-red-600 mt-1">
            Your AI checks this list before every outbound call
          </p>
        </div>

        {/* Upload Tabs */}
        <Tabs value={uploadMode} onValueChange={(value) => setUploadMode(value as 'single' | 'bulk')} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="single" className="flex items-center gap-2">
              <PhoneOff className="h-4 w-4" />
              Single Number
            </TabsTrigger>
            <TabsTrigger value="bulk" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Bulk Upload
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="single" className="space-y-4 border rounded-lg p-4">
            <h3 className="font-semibold">Add Single Number</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  placeholder="(555) 123-4567"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  type="tel"
                />
              </div>
              <div>
                <Label htmlFor="reason">Reason</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger id="reason">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer_requested">Customer requested</SelectItem>
                    <SelectItem value="wrong_number">Wrong number</SelectItem>
                    <SelectItem value="competitor">Competitor</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Any additional context..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
              />
            </div>
            <Button
              type="button"
              onClick={() => addNumberMutation.mutate()}
              disabled={!phoneNumber || addNumberMutation.isPending}
              className="w-full sm:w-auto"
            >
              {addNumberMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add to List
            </Button>
          </TabsContent>
          
          <TabsContent value="bulk" className="space-y-4 border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Bulk Upload Numbers</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={downloadTemplate}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download Template
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="bulk-reason">Default Reason for All Numbers</Label>
                <Select value={bulkReason} onValueChange={setBulkReason}>
                  <SelectTrigger id="bulk-reason">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer_requested">Customer requested</SelectItem>
                    <SelectItem value="wrong_number">Wrong number</SelectItem>
                    <SelectItem value="competitor">Competitor</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Upload CSV File</Label>
                <div className="flex items-center gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Choose CSV File
                  </Button>
                  <span className="text-sm text-gray-500">
                    CSV should have a "phone_number" column
                  </span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
              
              {csvData && (
                <div className="space-y-2">
                  <Label>Preview (first 5 rows)</Label>
                  <div className="bg-gray-50 p-3 rounded border text-sm font-mono max-h-32 overflow-y-auto">
                    {csvData.split('\n').slice(0, 6).join('\n')}
                    {csvData.split('\n').length > 6 && '\n...'}
                  </div>
                </div>
              )}
              
              {uploadProgress && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-blue-800">Upload Progress</span>
                    <span className="text-sm text-blue-600">
                      {uploadProgress.processed}/{uploadProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(uploadProgress.processed / uploadProgress.total) * 100}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <div className="flex items-center gap-1 text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      {uploadProgress.success} success
                    </div>
                    {uploadProgress.errors > 0 && (
                      <div className="flex items-center gap-1 text-red-600">
                        <AlertCircle className="h-4 w-4" />
                        {uploadProgress.errors} errors
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <Button
                type="button"
                onClick={() => bulkUploadMutation.mutate()}
                disabled={!csvData || bulkUploadMutation.isPending}
                className="w-full sm:w-auto"
              >
                {bulkUploadMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Upload {csvData ? `(${csvData.split('\n').length - 1} numbers)` : ''}
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Phone Number</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Added On</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    Loading blocked numbers...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10">
                    <div className="flex flex-col items-center gap-2">
                      <div className="text-red-600 font-medium">
                        {(error as Error).message}
                      </div>
                      {(error as Error).message.includes('being set up') && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => window.location.reload()}
                          className="mt-2"
                        >
                          Try Again
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-gray-500">
                    No numbers blocked yet. Your AI Employee can call all uploaded contacts.
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry: DoNotCallEntry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {entry.phone_number}
                        <Badge variant="destructive" className="text-xs">DNC</Badge>
                      </div>
                    </TableCell>
                    <TableCell>{formatReasonDisplay(entry.reason)}</TableCell>
                    <TableCell className="max-w-xs truncate">{entry.notes || '—'}</TableCell>
                    <TableCell>{new Date(entry.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteNumberMutation.mutate(entry.id)}
                        disabled={deleteNumberMutation.isPending}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4">
            <span className="text-sm text-gray-700">
              Page {page + 1} of {totalPages} ({totalCount} entries)
            </span>
            <div className="space-x-2">
              <Button
                type="button"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                variant="outline"
                size="sm"
              >
                Previous
              </Button>
              <Button
                type="button"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
                variant="outline"
                size="sm"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}